import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { List, Plus, Trash2 } from 'lucide-react';
import {
  PAYMENT_METHODS,
  deliveryScheduleLabel,
  dueDateFrom,
  paiseToRupees,
  parseRupees,
  type CreateOrderInput,
  type Customer,
  type Invoice,
  type OrderWithItems,
  type PaymentMethod,
} from '@tailoring-crm/shared';
import { PromisedDateWarning } from '../components/capacity/promised-date-warning.js';
import { BookingDoneDialog } from '../components/orders/booking-done-dialog.js';
import { CouponField } from '../components/orders/coupon-field.js';
import {
  EMPTY_FABRIC,
  GarmentFabricField,
  draftFabricPaise,
  toFabricInput,
  type DraftFabric,
} from '../components/orders/garment-fabric-field.js';
import {
  ProductLinesEditor,
  draftProductLinesPaise,
  toProductLineInputs,
  type DraftProductLine,
} from '../components/orders/product-lines-editor.js';
import { CustomerFormDialog } from '../components/customers/customer-form-dialog.js';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Textarea,
  Label,
  useToast,
} from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { useIssueInvoiceForOrder } from '../lib/invoices/use-invoices.js';
import { bookingCapacityRequest, bookingTaskType } from '../lib/capacity/capacity-request.js';
import { useCapacityCheck } from '../lib/capacity/use-capacity.js';
import { useCustomer, useCustomerSearch } from '../lib/customers/use-customers.js';
import { useCustomerProfiles } from '../lib/measurements/use-profiles.js';
import { useProducts } from '../lib/products/use-products.js';
import { useTemplates } from '../lib/measurements/use-templates.js';
import { useBookingDraft } from '../lib/orders/use-booking-draft.js';
import { useCreateOrder, useNextOrderNumber } from '../lib/orders/use-orders.js';
import { todayInputValue } from '../lib/attendance/attendance-day.js';
import { useOffline } from '../lib/offline/offline-context.js';
import { useDeliverySchedules } from '../lib/settings/use-delivery-schedules.js';
import { useStageConfiguration } from '../lib/workflow/use-kanban.js';
import { formatCurrency, formatDate, formatMobile } from '../lib/format.js';

/**
 * Booking, as one continuous counter interaction.
 *
 * A customer is standing there. The flow is: find them, add garments one after another,
 * review the total, save — and it has to be finishable in about two minutes on a phone. So it
 * is one scrolling page rather than a wizard: a wizard's "next" button is a place to lose the
 * thread, and a shop that has to go back two steps to fix a price will stop using the screen.
 *
 * On a laptop that one page splits into two columns — everything typed on the left, what the
 * bill comes to pinned on the right — because the total is the half a customer interrupts to
 * ask about, and it should be readable without scrolling away from the row being typed. Below
 * `lg` the columns stack back into the single phone-sized run, money last.
 *
 * Everything typed is written to session storage on the way through, so the back gesture that
 * lives on the edge of every Android screen cannot cost the shop the booking.
 *
 * The total shown here is a PREVIEW. The server computes the real one from the prices, and
 * this page sends prices — never a total. If the two ever disagreed, the server would be right.
 */

interface DraftItem {
  garmentTypeKey: string;
  quantity: string;
  price: string;
  measurementProfileId: string;
  promisedDate: string;
  notes: string;
  /**
   * The extras chosen on this garment, as product id → quantity. A map rather than a list
   * because the control is a set of checkboxes with a quantity beside each, and "is piping
   * ticked?" is the question every render asks.
   */
  addOns: Record<string, number>;
  /**
   * The cloth it is cut from. Optional in the type because a draft saved before the field
   * existed comes back without it — read through `?? EMPTY_FABRIC`.
   */
  fabric?: DraftFabric;
}

interface Draft {
  customerId: string;
  customerName: string;
  customerPhone: string;
  items: DraftItem[];
  /**
   * What is sold over the counter on this bill — fabric, readymade, accessories. Optional in the
   * type because a draft saved before product lines existed comes back without the field.
   */
  productLines?: DraftProductLine[];
  /**
   * The date the order was taken. Its own field rather than "today", because a slip written on
   * Saturday and typed in on Monday belongs to Saturday — and the order number's financial year
   * is drawn from it.
   */
  bookedAt: string;
  /** What the whole bill is promised for. The schedule below fills it in; typing wins. */
  promisedDate: string;
  /**
   * When the customer is coming back to try it on. On the ORDER rather than the garment: they
   * come in once and try on whatever is ready.
   */
  /**
   * The delivery speed picked, by name. Kept in the draft ONLY so the dropdown still reads back
   * the same after a back-gesture — it is a calculator for the due date and travels no further:
   * the payload carries the date it worked out, never the schedule.
   */
  deliveryScheduleName: string;
  priority: boolean;
  notes: string;
  discount: string;
  /**
   * The coupon code somebody typed, and what the server said it would do.
   *
   * BOTH live in the draft, because a back-gesture must bring back the figure the counter had
   * already quoted out loud — re-checking it silently on return could show a different number
   * if somebody else had spent the last of the code in the meantime.
   */
  couponCode: string;
  couponDiscountPaise: number;
  /** What the shop charges to deliver this one. Rupees as typed. */
  deliveryCharge: string;
  /** The advance, taken as part of the same conversation. Rupees as typed. */
  advance: string;
  advanceMethod: PaymentMethod;
}

const EMPTY_DRAFT: Draft = {
  customerId: '',
  customerName: '',
  customerPhone: '',
  items: [],
  productLines: [],
  bookedAt: '',
  promisedDate: '',
  deliveryScheduleName: '',
  priority: false,
  notes: '',
  discount: '',
  couponCode: '',
  couponDiscountPaise: 0,
  deliveryCharge: '',
  advance: '',
  advanceMethod: 'cash',
};

const EMPTY_ITEM: DraftItem = {
  garmentTypeKey: '',
  quantity: '1',
  price: '',
  measurementProfileId: '',
  promisedDate: '',
  notes: '',
  addOns: {},
  fabric: EMPTY_FABRIC,
};

/** Rupees as typed, in paise. Anything unparsable counts as nothing rather than blowing up. */
function paiseOf(input: string): number {
  if (input.trim() === '') return 0;
  try {
    return parseRupees(input);
  } catch {
    return 0;
  }
}

/**
 * A garment's own price, as the price box would hold it — and empty for one priced on the piece.
 *
 * Empty rather than 0, the same distinction `garmentTypeSchema` draws: "bridal lehengas, price on
 * the piece" is a real answer, and a 0 in this box would book a free lehenga rather than ask.
 */
function priceInputValue(paise: number | null): string {
  return paise === null ? '' : String(paiseToRupees(paise));
}

export function BookOrderPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();

  const { draft, setDraft, restored, clear } = useBookingDraft<Draft>(EMPTY_DRAFT);
  const [customerQuery, setCustomerQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** The add-a-customer form, opened without leaving the half-typed booking behind it. */
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  /**
   * The customer that form found instead of creating one.
   *
   * The counter typed a number that already belongs to somebody, said "yes, her", and on every
   * other screen that means "open her page". Here it cannot: there is a booking in progress on
   * this one. So the id is held until her record arrives and she is chosen in place.
   */
  const [existingCustomerId, setExistingCustomerId] = useState<string | null>(null);

  /**
   * Arriving from another screen (a customer's page), with the customer already
   * chosen.
   *
   * Seeded only when the draft has no customer yet, so a half-typed booking already in progress
   * is never overwritten by a stale link — a restored draft is somebody's unfinished work and
   * outranks a query string.
   */
  const [searchParams] = useSearchParams();
  const seededCustomerId = searchParams.get('customerId');
  useEffect(() => {
    if (!seededCustomerId) return;
    if (draft.customerId !== '') return;

    setDraft((previous) => ({
      ...previous,
      customerId: seededCustomerId,
      customerName: searchParams.get('customerName') ?? '',
      customerPhone: searchParams.get('customerPhone') ?? '',
      // Seeded into the order's own notes by whoever sent us here. Never over anything already
      // typed here.
      // Called `notes` because it names where it lands: this page has no reason to know what the
      // other screen called it.
      notes: previous.notes || (searchParams.get('notes') ?? ''),
    }));
  }, [seededCustomerId, draft.customerId, searchParams, setDraft]);

  const templates = useTemplates();
  const create = useCreateOrder();
  const issueBill = useIssueInvoiceForOrder();
  const { isOffline } = useOffline();
  /** Invoicing is money, which a tailor is refused on every route — so they are not offered it. */
  const { role } = useAuth();
  const mayIssueBill = role !== 'tailor';
  /**
   * Whether booking also issues the bill. `null` until somebody touches the box, and until then
   * it follows the booking: a counter sale is finished the moment it is booked, while a garment
   * may still change price at the fitting, and an issued bill can only be corrected by a credit
   * note. Held outside the draft on purpose — it is a decision about this press of the button.
   */
  const [issueBillChoice, setIssueBillChoice] = useState<boolean | null>(null);
  /** The booking just made, with the bill issued alongside it — the dialog's reason to open. */
  const [done, setDone] = useState<{ order: OrderWithItems; invoice: Invoice } | null>(null);

  /** WHO IS THIS FOR — the customer book, searched as the counter types. */
  const searching = customerQuery.trim().length > 0 && draft.customerId === '';
  const customers = useCustomerSearch({ q: customerQuery, pageSize: 5 }, { enabled: searching });

  /** Whether there is anything to hang under the box — an empty list must not draw a panel. */
  const matches = customers.data?.items ?? [];

  /**
   * "This is who it is for", said once.
   *
   * Three things choose a customer on this screen now — a search row, the form that just created
   * one, and the person that form found already had the number — and every one of them has to
   * leave the same draft behind and put the search away. Written once so the third one cannot
   * forget the half the other two do.
   */
  const chooseCustomer = (customer: Pick<Customer, 'id' | 'name' | 'primaryPhone'>): void => {
    setDraft((previous) => ({
      ...previous,
      customerId: customer.id,
      customerName: customer.name,
      customerPhone: customer.primaryPhone,
    }));
    setCustomerQuery('');
  };

  /**
   * Her record, fetched because the duplicate answer carries only an id and a name, and the
   * booking needs the number it will print on the bill to be HERS rather than the one that was
   * typed — the two are different whenever the match was on an alternate number.
   */
  const existingCustomer = useCustomer(existingCustomerId);
  useEffect(() => {
    const found = existingCustomer.data;
    if (!found || found.id !== existingCustomerId) return;
    setDraft((previous) => ({
      ...previous,
      customerId: found.id,
      customerName: found.name,
      customerPhone: found.primaryPhone,
    }));
    setCustomerQuery('');
    setExistingCustomerId(null);
  }, [existingCustomer.data, existingCustomerId, setDraft]);

  /**
   * The number this booking is about to be written under.
   *
   * Shown before the order exists because the counter writes it on the slip pinned to the cloth
   * while the customer is still standing there — finding it out on the next screen means either
   * a second trip to the counter or a bundle with no number on it. It is a peek at the shop's
   * series and NOT a reservation, which is why the line under it says so and why an abandoned
   * booking costs the shop nothing.
   *
   * Not asked for offline: the number would be a guess made against a counter this device
   * cannot see, and a queued booking takes its number when it finally reaches the server.
   */
  const nextNumber = useNextOrderNumber({ enabled: !isOffline });

  // The chosen customer's saved measurements, so a garment can be booked against one.
  const profiles = useCustomerProfiles(draft.customerId || null);
  /**
   * The extras the shop sells — buttons, laces, falls, linings. `accessory` products, which is
   * what the catalogue already calls them, so there is no second list to keep in step.
   */
  // Retired lines are excluded by the endpoint's own default, which is what a booking screen
  // wants: a shop that stopped selling zari lace should not be offered it at the counter.
  const accessoryProducts = useProducts({ category: 'accessory' });
  /** Everything the counter can sell as a product line; the editor keeps to the goods. */
  const allProducts = useProducts();
  const productLines = useMemo(() => draft.productLines ?? [], [draft.productLines]);
  const setProductLines = (next: DraftProductLine[]): void =>
    setDraft((previous) => ({ ...previous, productLines: next }));

  /**
   * Garments plus their extras, priced the way the server prices them when it books: an extra
   * is its catalogue rate times its OWN quantity, not the garment's — two shirts with one set of
   * designer buttons is one set on the bill.
   */
  const subtotalPaise = useMemo(() => {
    const addOnRates = new Map(
      (accessoryProducts.data?.items ?? []).map((product) => [
        product.id,
        product.unitPricePaise ?? 0,
      ]),
    );
    return draft.items.reduce((sum, item) => {
      const garments = paiseOf(item.price) * (Number(item.quantity) || 0);
      const extras = Object.entries(item.addOns).reduce(
        (total, [productId, quantity]) => total + (addOnRates.get(productId) ?? 0) * quantity,
        0,
      );
      return sum + garments + extras + draftFabricPaise(item.fabric);
    }, draftProductLinesPaise(productLines));
  }, [draft.items, productLines, accessoryProducts.data]);
  /**
   * The two halves of the discount, added exactly as the server adds them in `recomputeOrder`.
   * The coupon figure is whatever the preview said — never worked out here — because a
   * percentage, its cap and its minimum are the shop's rules and the browser must not own a
   * second copy of them.
   */
  const manualDiscountPaise = paiseOf(draft.discount);
  const couponDiscountPaise = draft.couponCode ? draft.couponDiscountPaise : 0;
  const discountPaise = Math.min(manualDiscountPaise + couponDiscountPaise, subtotalPaise);
  // A draft written before delivery charges existed has no such field.
  const deliveryChargePaise = paiseOf(draft.deliveryCharge ?? '');
  // The same arithmetic the server does in `orderTotals`, and in the same order — the discount
  // comes off the stitching, and delivery is added whole.
  const totalPaise = subtotalPaise + deliveryChargePaise - discountPaise;
  // A draft written before advances existed has no such field; treat it as no advance rather
  // than handing an undefined to a controlled input.
  const advancePaise = paiseOf(draft.advance ?? '');
  const advanceTooMuch = advancePaise > totalPaise;

  /**
   * The advisory capacity check behind the promised date. ADVISORY IS THE WHOLE OF IT: nothing
   * below this comment reaches the submit button, the validation or the payload, and the booking
   * behaves identically whether the check says yes, says no, fails or never fires.
   *
   * It asks only when there is a question — a chosen date and at least one garment — and the
   * question it asks is built from three primitives, so a price or a note typed into a garment
   * line does not re-ask it. Offline it does not fire at all, and neither a failure nor a refusal
   * puts anything on the screen: see `promised-date-warning.tsx`.
   */
  /**
   * The shop's delivery speeds, and the two handlers that keep the three date controls honest.
   *
   * The rule is one-directional: a schedule WRITES the due date, and nothing ever reads back the
   * other way. Picking "Express" sets the date; moving the order date recomputes it while a
   * schedule is chosen; typing a date by hand clears the schedule. What is never done is
   * inferring a schedule from a date somebody typed — two schedules can be three days apart on
   * the same week and guessing between them would put a promise on the screen the shop never
   * made.
   */
  const schedules = useDeliverySchedules();

  /** Captured once per visit: a booking should not change its own order date mid-typing. */
  const today = useMemo(() => todayInputValue(), []);

  const applySchedule = (name: string): void => {
    const chosen = (schedules.data ?? []).find((schedule) => schedule.name === name);
    setDraft((previous) => {
      if (!chosen) return { ...previous, deliveryScheduleName: '' };
      const from = previous.bookedAt || today;
      return {
        ...previous,
        deliveryScheduleName: chosen.name,
        promisedDate: dueDateFrom(from, chosen.days) ?? previous.promisedDate,
      };
    });
  };

  const setBookedAt = (value: string): void => {
    setDraft((previous) => {
      const chosen = (schedules.data ?? []).find(
        (schedule) => schedule.name === previous.deliveryScheduleName,
      );
      // Only while a schedule is doing the arithmetic. A hand-typed due date is the shop's own
      // promise and back-dating the slip must not quietly move it.
      if (!chosen || !value) return { ...previous, bookedAt: value };
      return {
        ...previous,
        bookedAt: value,
        promisedDate: dueDateFrom(value, chosen.days) ?? previous.promisedDate,
      };
    });
  };

  /**
   * Today, written into the draft rather than defaulted at render.
   *
   * A field whose displayed value falls back to today cannot be cleared — the fallback puts it
   * straight back and the counter cannot back-date a slip at all. Seeding once leaves an
   * ordinary controlled input behind, and `restored` guards the case that matters: a draft
   * resumed at 12:01am must keep the date it was started under, not silently roll to the new
   * day while the customer is still standing there.
   */
  const seededOrderDate = useRef(false);
  useEffect(() => {
    // ONCE, and guarded by a ref rather than by "is the field empty?". Emptiness is a state the
    // counter can put the field into — clearing it to retype a back-date — and a condition that
    // re-seeds on empty would push today back in under their fingers.
    if (seededOrderDate.current) return;
    seededOrderDate.current = true;
    if (restored) return;
    setDraft((previous) => (previous.bookedAt ? previous : { ...previous, bookedAt: today }));
  }, [restored, setDraft, today]);

  const stageConfiguration = useStageConfiguration();
  const enabledStages = stageConfiguration.data?.enabledStages;
  const garmentLineCount = draft.items.filter((item) => item.garmentTypeKey !== '').length;
  const issueBillNow = mayIssueBill && !isOffline && (issueBillChoice ?? garmentLineCount === 0);
  const capacityTaskType = useMemo(() => bookingTaskType(enabledStages ?? []), [enabledStages]);
  const capacityRequest = useMemo(
    () =>
      bookingCapacityRequest({
        deliveryDay: draft.promisedDate,
        garmentLineCount,
        taskType: capacityTaskType,
      }),
    [draft.promisedDate, garmentLineCount, capacityTaskType],
  );
  const capacity = useCapacityCheck(capacityRequest, { enabled: !isOffline });
  const capacityAdvice = !isOffline && capacity.isSuccess ? capacity.data : null;

  const accessories = accessoryProducts.data?.items ?? [];

  /** Ticking an extra on, or off. Unticking forgets the quantity, which is what "off" means. */
  const toggleAddOn = (index: number, productId: string): void => {
    setDraft((previous) => ({
      ...previous,
      items: previous.items.map((item, position) => {
        if (position !== index) return item;
        const next = { ...item.addOns };
        if (productId in next) delete next[productId];
        else next[productId] = 1;
        return { ...item, addOns: next };
      }),
    }));
  };

  /**
   * Below one is not a quantity; it is how somebody clears the box on the way to typing 2. It is
   * held as 0 — an empty box — rather than snapped back to 1 on the keystroke, which turned the
   * "3" typed next into 13. Leaving the box empty puts the 1 back (`settleAddOnQuantity`).
   */
  const setAddOnQuantity = (index: number, productId: string, quantity: number): void => {
    const safe = Number.isFinite(quantity) && quantity >= 1 ? Math.floor(quantity) : 0;
    setDraft((previous) => ({
      ...previous,
      items: previous.items.map((item, position) =>
        position === index ? { ...item, addOns: { ...item.addOns, [productId]: safe } } : item,
      ),
    }));
  };

  /** An extra left ticked with an empty box is one of it — ticked means at least one. */
  const settleAddOnQuantity = (index: number, productId: string): void => {
    setDraft((previous) => ({
      ...previous,
      items: previous.items.map((item, position) =>
        position === index && item.addOns[productId] === 0
          ? { ...item, addOns: { ...item.addOns, [productId]: 1 } }
          : item,
      ),
    }));
  };

  const setItem = (index: number, patch: Partial<DraftItem>): void => {
    setDraft((previous) => ({
      ...previous,
      items: previous.items.map((item, position) =>
        position === index ? { ...item, ...patch } : item,
      ),
    }));
  };

  /**
   * Picking the garment, and taking its price with it.
   *
   * A shop that has said on the garments screen that a blouse is ₹800 should never type ₹800
   * again. It is a DEFAULT and not a ceiling: the box stays editable,
   * because "her lehenga at 12k because she is bringing her sister" is the most ordinary sentence
   * said over this screen.
   *
   * WHAT THE SHOP TYPED IS NEVER OVERWRITTEN, and that is the whole reason this is not a one-line
   * patch. The garment can be changed on a line that already has a figure in it, and there are
   * two quite different reasons for one to be there: the last garment put it there, or a person
   * did. A price that still reads exactly as the PREVIOUS garment's price is the first kind and
   * is replaced; anything else is somebody's own number and is left alone, even though the
   * garment beside it just changed. Comparing against the old garment rather than keeping a
   * "touched" flag also keeps `DraftItem` as it is — the draft is round-tripped through session
   * storage, and a new field there is a new shape for every half-finished booking already saved.
   *
   * Clearing the garment back to the blank option clears an auto-filled price with it, for the
   * same reason: it was the garment's, and there is no longer a garment.
   */
  const chooseGarment = (index: number, garmentTypeKey: string): void => {
    const item = draft.items[index];
    if (!item) return;

    const all = templates.data?.templates ?? [];
    const chosen = all.find((template) => template.garmentTypeKey === garmentTypeKey);
    const previous = all.find((template) => template.garmentTypeKey === item.garmentTypeKey);
    const typedByHand =
      item.price.trim() !== '' && item.price !== priceInputValue(previous?.unitPricePaise ?? null);

    setItem(index, {
      garmentTypeKey,
      ...(typedByHand ? {} : { price: priceInputValue(chosen?.unitPricePaise ?? null) }),
    });
  };

  const submit = async (): Promise<void> => {
    setError(null);
    if (!draft.customerId) {
      setError(t('orders.booking.needCustomer'));
      return;
    }
    const usable = draft.items.filter((item) => item.garmentTypeKey !== '');
    const productLineInputs = toProductLineInputs(productLines);
    // A bill of goods alone is a counter sale; a bill of nothing at all is a mistake.
    if (usable.length === 0 && productLineInputs.length === 0) {
      setError(t('orders.booking.needGarmentOrProduct'));
      return;
    }
    // The server refuses an advance larger than the bill anyway; saying so here saves the shop
    // a round trip at the busiest moment of its day.
    if (advanceTooMuch) {
      setError(t('orders.booking.advanceTooMuch'));
      return;
    }

    const payload: CreateOrderInput = {
      customerId: draft.customerId,
      promisedDate: draft.promisedDate || null,
      // Sent only when the slip is BACK-DATED. The field reads as today either way, and a
      // booking made today should carry the time of day the server stamps on it rather than a
      // midnight this page worked out — so "today" is sent as nothing at all.
      ...(draft.bookedAt && draft.bookedAt !== today ? { bookedAt: draft.bookedAt } : {}),
      priority: draft.priority,
      notes: draft.notes.trim() || null,
      // The TYPED half only. The coupon is named, and the server prices it against the real
      // subtotal inside the booking transaction — which is what makes a usage limit mean what
      // it says when two counters book at the same second.
      discountPaise: manualDiscountPaise,
      ...(draft.couponCode ? { couponCode: draft.couponCode } : {}),
      deliveryChargePaise,
      // Recorded in the same transaction as the booking, so the shop never has a bill it
      // thinks was paid for, or a payment against an order that was never written.
      ...(advancePaise > 0
        ? { advance: { amountPaise: advancePaise, method: draft.advanceMethod ?? 'cash' } }
        : {}),
      items: usable.map((item) => ({
        garmentTypeKey: item.garmentTypeKey,
        quantity: Number(item.quantity) || 1,
        unitPricePaise: paiseOf(item.price),
        promisedDate: item.promisedDate || null,
        addOns: Object.entries(item.addOns).map(([productId, quantity]) => ({
          productId,
          // Booked straight from an emptied box, before it lost focus.
          quantity: Math.max(1, quantity),
        })),
        ...(toFabricInput(item.fabric) ? { fabric: toFabricInput(item.fabric) } : {}),
        notes: item.notes.trim() || null,
        measurementProfileId: item.measurementProfileId || null,
      })),
      productLines: productLineInputs,
    };

    try {
      const order = await create.mutateAsync(payload);
      // The draft has done its job; leaving it behind would greet the next booking with the
      // last one's garments.
      clear();
      toast({
        title: t('orders.booking.booked', { number: order.orderNumber }),
        variant: 'success',
      });
      // Sold anyway — the shelf count is often wrong — but the shop should know it is.
      for (const warning of order.stockWarnings ?? []) {
        toast({
          title: t('orders.productLines.stockShort', { name: warning.name }),
          variant: 'warning',
        });
      }
      if (issueBillNow) {
        try {
          const invoice = await issueBill.mutateAsync({ orderId: order.id });
          setDone({ order, invoice });
          return;
        } catch (caught) {
          // The order is booked either way; the bill can be issued again from its page.
          toast({
            title: caught instanceof Error ? caught.message : t('error.unexpected'),
            variant: 'destructive',
          });
        }
      }
      navigate(`/orders/${order.id}`);
    } catch (caught) {
      setError(
        isApiRequestError(caught) || caught instanceof Error
          ? caught.message
          : t('error.unexpected'),
      );
    }
  };

  return (
    <>
      {/* The sidebar's Orders entry lands here, so this is the way back to an order already
          booked — to assign its tailors or move its stage. A bare arrow hid that; the words say it. */}
      <header className="flex items-center justify-between gap-2 pb-5">
        <h2 className="text-xl font-semibold leading-tight">{t('orders.booking.addNewOrder')}</h2>
        <Link
          to="/orders"
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <List aria-hidden="true" className="size-4" />
          {t('orders.booking.backToOrders')}
        </Link>
      </header>

      <div className="flex flex-col gap-4">
        {error ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
          >
            {error}
          </p>
        ) : null}

        {/*
          Two columns on a laptop, one on a phone.

          What is on the left is everything the counter TYPES; what is on the right is what the
          bill currently comes to. Splitting them that way is worth a column because the money is
          the half a customer asks about mid-sentence — "and with the blouse?" — and on the old
          single column the answer was below the fold behind however many garments had been added.
          Pinned, it is answerable without scrolling away from the row being typed.

          Below `lg` the two stack and the money lands last, which is the order the conversation
          actually runs in on a phone: add the garments, then read out the total.
        */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            {/* 1. Who is it for. Search-first, exactly as the customer screen is. */}
            <Card>
              {/* No card heading over these first two cards. "Customer" above "Who is this
                  for?" is the same sentence twice, and the field labels already name the card. */}
              <CardContent className="flex flex-col gap-2 pt-4 sm:pt-6">
                {/*
                  ONE ROW: who it is for, the way to add somebody who is not in the book yet, and
                  the number this booking will be written under.

                  Three equal columns on anything wider than a phone, because those are the three
                  things the counter deals with before a single garment is typed and they belong
                  on one line rather than stacked down a page. `items-end` sits the button on the
                  inputs' own baseline — the two fields carry labels above them and the button
                  does not, so aligning on the top edge would leave it floating.

                  On a phone they stack in that same order, which is the order the conversation
                  runs in: find her, add her if she is new, read the number back.
                */}
                <div className="grid grid-cols-1 items-end gap-3 sm:grid-cols-3">
                  {/* 1. Who it is for. `relative`, so the results hang under the box as a list
                      rather than pushing the other two columns down the page as they arrive. */}
                  <div className="relative flex flex-col gap-1.5">
                    <Label htmlFor="booking-customer">{t('orders.booking.chooseCustomer')}</Label>
                    {draft.customerId ? (
                      /*
                       * The chosen customer, wearing the field she was found in — same height,
                       * same border, so the row does not change shape when she is picked.
                       *
                       * A real button rather than a filled-in input, because it does something:
                       * pressing it puts the search back. The accessible name stays "Change" —
                       * what the control DOES — while the face of it is who is standing there.
                       */
                      <button
                        type="button"
                        aria-label={t('orders.booking.changeCustomer')}
                        className="flex h-11 w-full items-center rounded-md border border-input bg-background px-3 py-2 text-left text-base ring-offset-background hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:text-sm"
                        onClick={() =>
                          setDraft((previous) => ({
                            ...previous,
                            customerId: '',
                            customerName: '',
                            customerPhone: '',
                            items: previous.items.map((item) => ({
                              ...item,
                              measurementProfileId: '',
                            })),
                          }))
                        }
                      >
                        <span className="truncate">
                          {draft.customerName} ({formatMobile(draft.customerPhone)})
                        </span>
                      </button>
                    ) : (
                      <>
                        <Input
                          id="booking-customer"
                          type="search"
                          value={customerQuery}
                          onChange={(event) => setCustomerQuery(event.target.value)}
                          placeholder={t('orders.booking.searchCustomer')}
                        />

                        {/* Over the page, not in it: what is being typed keeps its place on the
                            row while the matches come and go underneath. */}
                        {matches.length > 0 ? (
                          <ul className="absolute left-0 right-0 top-full z-20 mt-1 flex max-h-72 flex-col gap-1 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg">
                            {(customers.data?.items ?? []).map((customer) => (
                              <li key={customer.id}>
                                <button
                                  type="button"
                                  className="flex w-full flex-col rounded-md p-2 text-left hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  onClick={() => chooseCustomer(customer)}
                                >
                                  <span className="font-medium">{customer.name}</span>
                                  <span className="text-sm text-muted-foreground">
                                    {formatMobile(customer.primaryPhone)}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </>
                    )}
                  </div>

                  {/*
                    2. The walk-in who is in neither book. Always on the row rather than appearing
                    once the search comes back empty — the counter reaches for it knowing already
                    that this person has never been here, and a control that moves is a control
                    that has to be found again.

                    Offline it is refused: a booking can be queued because it names a customer who
                    already exists, but a new one cannot — the order about to be written needs an
                    id only the server can mint.
                  */}
                  <Button disabled={isOffline} onClick={() => setCreatingCustomer(true)}>
                    {t('orders.booking.addCustomer')}
                  </Button>

                  {/* 3. The number this will be booked under, in a box of its own because it is
                      copied onto the slip that goes with the cloth. Read-only: it is the shop's
                      series that decides it, never the counter. */}
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="booking-order-id">{t('orders.booking.orderNumberLabel')}</Label>
                    <Input
                      id="booking-order-id"
                      readOnly
                      value={nextNumber.data?.orderNumber ?? ''}
                      placeholder={t('orders.booking.orderNumberUnknown')}
                      className="font-medium tabular-nums"
                    />
                  </div>
                </div>

                {/* Said plainly under the row, because it is true: the number is not taken until
                    the booking is saved, and the shop's other counter may take it first. */}
                {nextNumber.data ? (
                  <p className="text-sm text-muted-foreground">
                    {t('orders.booking.orderNumberProvisional')}
                  </p>
                ) : null}

                {/* Stated rather than left as a disabled button with no reason. */}
                {isOffline ? (
                  <p className="text-sm text-muted-foreground">
                    {t('orders.booking.newCustomersNeedConnection')}
                  </p>
                ) : null}
              </CardContent>
            </Card>

            {/* 2. When it is promised, and anything said about the order as a whole. */}
            <Card>
              <CardContent className="flex flex-col gap-3 pt-4 sm:pt-6">
                {/* The three the counter fills in together: how fast, from when, until when.
                    The schedule sits first because picking one answers the third. */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="delivery-schedule">
                      {t('orders.booking.deliverySchedule')}
                    </Label>
                    <select
                      id="delivery-schedule"
                      value={draft.deliveryScheduleName ?? ''}
                      onChange={(event) => applySchedule(event.target.value)}
                      className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                    >
                      <option value="">{t('orders.booking.deliveryScheduleNone')}</option>
                      {(schedules.data ?? []).map((schedule) => (
                        <option key={schedule.name} value={schedule.name}>
                          {deliveryScheduleLabel(schedule)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="order-booked-at">{t('orders.booking.orderDate')}</Label>
                    <Input
                      id="order-booked-at"
                      type="date"
                      value={draft.bookedAt}
                      onChange={(event) => setBookedAt(event.target.value)}
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="order-date">{t('orders.booking.dueDate')}</Label>
                    <Input
                      id="order-date"
                      type="date"
                      value={draft.promisedDate}
                      onChange={(event) =>
                        // Typed by hand, so the schedule that suggested it no longer describes
                        // it. Clearing the dropdown says so rather than leaving it claiming a
                        // date it did not produce.
                        setDraft((previous) => ({
                          ...previous,
                          promisedDate: event.target.value,
                          deliveryScheduleName: '',
                        }))
                      }
                    />
                  </div>
                </div>

                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={draft.priority}
                    onChange={(event) =>
                      setDraft((previous) => ({ ...previous, priority: event.target.checked }))
                    }
                  />
                  {t('orders.booking.priority')}
                </label>

                {/* Advice, not a rule. The input above gains no `min` and is never rewritten from
                    here — the earliest date goes in only when somebody presses the control. Full
                    width under the row rather than inside the date's column: it runs to two lines
                    and carries a button, and squeezing that into half a card wraps it badly. */}
                <PromisedDateWarning
                  result={capacityAdvice}
                  onUseEarliestDate={(day) =>
                    setDraft((previous) => ({ ...previous, promisedDate: day }))
                  }
                />

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="order-notes">{t('orders.booking.orderNotes')}</Label>
                  <Textarea
                    id="order-notes"
                    value={draft.notes}
                    onChange={(event) =>
                      setDraft((previous) => ({ ...previous, notes: event.target.value }))
                    }
                  />
                </div>
              </CardContent>
            </Card>

            {/* 3. The garments, added one after another without leaving the page. */}
            <Card>
              <CardHeader className="flex-row items-center justify-between gap-2 pb-3 sm:pb-4">
                {/* Counted in the heading: on a long booking the row being typed is the only one
                    on screen, and "how many did I put on this?" should not need a scroll. */}
                <CardTitle>
                  {t('orders.booking.garmentsCount', { count: draft.items.length })}
                </CardTitle>
                <Button
                  size="sm"
                  onClick={() =>
                    setDraft((previous) => ({
                      ...previous,
                      items: [...previous.items, { ...EMPTY_ITEM }],
                    }))
                  }
                >
                  <Plus aria-hidden="true" />
                  {t('orders.booking.addGarment')}
                </Button>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {draft.items.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground">
                    {t('orders.booking.noGarments')} {t('orders.booking.noGarmentsHint')}
                  </p>
                ) : (
                  <ul className="flex flex-col gap-3">
                    {draft.items.map((item, index) => (
                      <li
                        key={index}
                        className="flex flex-col gap-3 rounded-lg border border-border p-3"
                      >
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor={`garment-${index}`}>
                              {t('orders.booking.garmentType')}
                            </Label>
                            <select
                              id={`garment-${index}`}
                              value={item.garmentTypeKey}
                              onChange={(event) => chooseGarment(index, event.target.value)}
                              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                            >
                              <option value="" />
                              {(templates.data?.templates ?? []).map((template) => (
                                <option
                                  key={template.garmentTypeKey}
                                  value={template.garmentTypeKey}
                                >
                                  {template.garmentTypeLabel}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor={`quantity-${index}`}>
                              {t('orders.booking.quantity')}
                            </Label>
                            <Input
                              id={`quantity-${index}`}
                              inputMode="numeric"
                              value={item.quantity}
                              onChange={(event) => setItem(index, { quantity: event.target.value })}
                            />
                          </div>

                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor={`price-${index}`}>{t('orders.booking.price')}</Label>
                            <Input
                              id={`price-${index}`}
                              inputMode="decimal"
                              value={item.price}
                              onChange={(event) => setItem(index, { price: event.target.value })}
                            />
                          </div>
                        </div>

                        {/* The measurements to FREEZE onto this garment. */}
                        <div className="flex flex-col gap-1.5">
                          <Label htmlFor={`measurements-${index}`}>
                            {t('orders.booking.measurements')}
                          </Label>
                          <select
                            id={`measurements-${index}`}
                            value={item.measurementProfileId}
                            onChange={(event) =>
                              setItem(index, { measurementProfileId: event.target.value })
                            }
                            className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                          >
                            <option value="">{t('orders.booking.noProfile')}</option>
                            {(profiles.data ?? [])
                              .filter(
                                (profile) =>
                                  item.garmentTypeKey === '' ||
                                  profile.garmentTypeKey === item.garmentTypeKey,
                              )
                              .map((profile) => (
                                <option key={profile.id} value={profile.id}>
                                  {profile.name} — {formatDate(profile.takenAt)}
                                </option>
                              ))}
                          </select>
                        </div>

                        {/* The extras. Only shown when the shop actually sells any — a
                            boutique that has never added an accessory should not carry an
                            empty band on every garment it books. */}
                        {accessories.length > 0 ? (
                          <div className="flex flex-col gap-1.5">
                            <Label>{t('orders.booking.addOns')}</Label>
                            <div className="flex flex-wrap gap-2">
                              {accessories.map((accessory) => {
                                const quantity = item.addOns[accessory.id];
                                const on = quantity !== undefined;
                                return (
                                  <label
                                    key={accessory.id}
                                    className="flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={on}
                                      onChange={() => toggleAddOn(index, accessory.id)}
                                      aria-label={t('orders.booking.addOnToggle', {
                                        name: accessory.name,
                                      })}
                                    />
                                    <span>{accessory.name}</span>
                                    <span className="text-muted-foreground">
                                      {formatCurrency(accessory.unitPricePaise ?? 0)}
                                    </span>
                                    {on ? (
                                      <input
                                        type="number"
                                        min={1}
                                        value={quantity || ''}
                                        onBlur={() => settleAddOnQuantity(index, accessory.id)}
                                        onChange={(event) =>
                                          setAddOnQuantity(
                                            index,
                                            accessory.id,
                                            Number(event.target.value),
                                          )
                                        }
                                        aria-label={t('orders.booking.addOnQuantity', {
                                          name: accessory.name,
                                        })}
                                        className="w-14 rounded border border-input bg-background px-1.5 py-0.5 text-sm"
                                      />
                                    ) : null}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}

                        {/* The cloth, beside the extras: both are what the garment is made of,
                            and both are on the bill when the shop supplied them. */}
                        <GarmentFabricField
                          idPrefix={`fabric-${index}`}
                          fabric={item.fabric ?? EMPTY_FABRIC}
                          onChange={(fabric) => setItem(index, { fabric })}
                          products={allProducts.data?.items ?? []}
                        />

                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor={`item-date-${index}`}>
                              {t('orders.booking.itemPromisedDate')}
                            </Label>
                            <Input
                              id={`item-date-${index}`}
                              type="date"
                              value={item.promisedDate}
                              onChange={(event) =>
                                setItem(index, { promisedDate: event.target.value })
                              }
                            />
                          </div>
                          <div className="flex flex-col gap-1.5">
                            <Label htmlFor={`item-notes-${index}`}>
                              {t('orders.booking.itemNotes')}
                            </Label>
                            <Input
                              id={`item-notes-${index}`}
                              value={item.notes}
                              onChange={(event) => setItem(index, { notes: event.target.value })}
                            />
                          </div>
                        </div>

                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            aria-label={t('orders.booking.removeGarment', { position: index + 1 })}
                            onClick={() =>
                              setDraft((previous) => ({
                                ...previous,
                                items: previous.items.filter((_, position) => position !== index),
                              }))
                            }
                          >
                            <Trash2 aria-hidden="true" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* 3b. What is sold over the counter on this bill. Its own card, under the garments,
                because it is a different kind of line: no measurements, no tailor, no stage. */}
            <Card>
              <CardHeader className="pb-3 sm:pb-4">
                <CardTitle>
                  {t('orders.productLines.heading', { count: productLines.length })}
                </CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-3">
                {garmentLineCount === 0 && toProductLineInputs(productLines).length > 0 ? (
                  <p className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
                    {t('orders.productLines.quickSaleHint')}
                  </p>
                ) : null}
                <ProductLinesEditor
                  lines={productLines}
                  onChange={setProductLines}
                  products={allProducts.data?.items ?? []}
                />
              </CardContent>
            </Card>
          </div>

          {/*
            4. The money, and the button that commits it.

            The advance lives in here rather than on a second screen: the customer is standing at
            the counter with their money out, and asking them to wait while somebody reopens the
            order they just booked is how a shop ends up recording it "later", or not at all. It
            is written in the same transaction as the booking.

            `self-start` matters — a stretched flex item cannot stick. A `div` and not an `aside`,
            because the sidebar is already this page's one `complementary` landmark and a second
            unlabelled one is a stop a screen reader has to walk past on every booking.
          */}
          <div className="lg:sticky lg:top-4 lg:w-[22rem] lg:shrink-0 lg:self-start">
            <Card>
              <CardHeader className="pb-3 sm:pb-4">
                <CardTitle>{t('orders.booking.paymentCard')}</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-col gap-5">
                {/* The ledger, on Juvee's rhythm: one label-left / figure-right row per line,
                    spaced far enough apart to be read down rather than scanned as a block. */}
                <div className="flex flex-col gap-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-muted-foreground">{t('orders.booking.subtotal')}</span>
                    <span className="tabular-nums">{formatCurrency(subtotalPaise)}</span>
                  </div>

                  {/* Typed here, beside the discount, because it moves the total the same way:
                      the counter sees the bill change as they type it. */}
                  <div className="flex items-center justify-between gap-3">
                    <Label
                      htmlFor="order-delivery-charge"
                      className="font-normal text-muted-foreground"
                    >
                      {t('orders.booking.deliveryCharge')}
                    </Label>
                    <Input
                      id="order-delivery-charge"
                      inputMode="decimal"
                      className="h-9 w-24 text-right"
                      value={draft.deliveryCharge ?? ''}
                      onChange={(event) =>
                        setDraft((previous) => ({
                          ...previous,
                          deliveryCharge: event.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="order-discount" className="font-normal text-muted-foreground">
                      {t('orders.booking.discount')}
                    </Label>
                    <div className="flex items-center gap-2">
                      {discountPaise > 0 ? (
                        <span className="tabular-nums text-muted-foreground">
                          −{formatCurrency(discountPaise)}
                        </span>
                      ) : null}
                      <Input
                        id="order-discount"
                        inputMode="decimal"
                        className="h-9 w-24 text-right"
                        value={draft.discount}
                        onChange={(event) =>
                          setDraft((previous) => ({ ...previous, discount: event.target.value }))
                        }
                      />
                    </div>
                  </div>

                  {/*
                    The coupon, under the discount it adds to rather than beside it. A code is
                    checked against the SERVER before the figure appears: the percentage, its
                    cap and its minimum are the shop's rules, and a browser working them out
                    for itself would be a second copy that eventually disagrees.
                  */}
                  <CouponField
                    subtotalPaise={subtotalPaise}
                    customerId={draft.customerId}
                    code={draft.couponCode}
                    discountPaise={draft.couponDiscountPaise}
                    onApplied={(code, applied) =>
                      setDraft((previous) => ({
                        ...previous,
                        couponCode: code,
                        couponDiscountPaise: applied,
                      }))
                    }
                  />

                  <div className="flex items-center justify-between gap-3 border-t border-border pt-3 text-base font-semibold">
                    <span>{t('orders.booking.totalIs')}</span>
                    <span className="tabular-nums">{formatCurrency(totalPaise)}</span>
                  </div>
                </div>

                <div className="flex flex-col gap-3 border-t border-border pt-5">
                  <div>
                    <h3 className="text-sm font-semibold">{t('orders.booking.advanceHeading')}</h3>
                    <p className="text-sm text-muted-foreground">
                      {t('orders.booking.advanceHint')}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="order-advance">{t('orders.booking.advanceAmount')}</Label>
                    <Input
                      id="order-advance"
                      inputMode="decimal"
                      value={draft.advance ?? ''}
                      onChange={(event) =>
                        setDraft((previous) => ({ ...previous, advance: event.target.value }))
                      }
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="order-advance-method">{t('payments.method')}</Label>
                    <select
                      id="order-advance-method"
                      value={draft.advanceMethod ?? 'cash'}
                      onChange={(event) =>
                        setDraft((previous) => ({
                          ...previous,
                          advanceMethod: event.target.value as PaymentMethod,
                        }))
                      }
                      className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
                    >
                      {PAYMENT_METHODS.map((one) => (
                        <option key={one} value={one}>
                          {t(`payments.methods.${one}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  {advanceTooMuch ? (
                    <p role="alert" className="text-sm font-medium text-destructive">
                      {t('orders.booking.advanceTooMuch')}
                    </p>
                  ) : null}

                  {/* The figure the customer is about to be asked for. Given the weight of a
                      row of its own rather than a ledger line, because it is the one number
                      that gets said out loud. */}
                  {advancePaise > 0 && !advanceTooMuch ? (
                    <div className="flex items-center justify-between gap-3 rounded-md bg-destructive/10 px-3 py-2.5 text-sm font-semibold text-destructive">
                      <span>{t('orders.booking.balanceAfterAdvance')}</span>
                      <span className="tabular-nums">
                        {formatCurrency(totalPaise - advancePaise)}
                      </span>
                    </div>
                  ) : null}
                </div>

                <p className="text-sm text-muted-foreground">{t('orders.booking.totalHint')}</p>

                {mayIssueBill ? (
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={issueBillNow}
                      disabled={isOffline}
                      onChange={(event) => setIssueBillChoice(event.target.checked)}
                    />
                    <span>
                      <span className="font-medium">{t('orders.booking.issueBillNow')}</span>
                      <span className="block text-muted-foreground">
                        {t('orders.booking.issueBillNowHint')}
                      </span>
                    </span>
                  </label>
                ) : null}

                <Button
                  block
                  data-tour="order-save"
                  disabled={create.isPending || issueBill.isPending}
                  onClick={() => void submit()}
                >
                  {t('orders.booking.save')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {done ? (
        <BookingDoneDialog
          order={done.order}
          invoice={done.invoice}
          onClose={() => navigate(`/orders/${done.order.id}`)}
        />
      ) : null}

      {/*
        Adding the walk-in without leaving the booking.

        The same form the customer book uses, so a customer created at the counter is not a
        thinner record than one created anywhere else — the shop's own fields, the tags and the
        duplicate check all come with it. What differs is only the ending: saved, she is chosen
        into this draft rather than opened, and the duplicate she turned out to be is chosen too
        instead of navigated to. Either way the garments already typed are still on the screen
        behind the dialog.
      */}
      <CustomerFormDialog
        open={creatingCustomer}
        onOpenChange={setCreatingCustomer}
        prefillQuery={customerQuery}
        onSaved={(customer) => chooseCustomer(customer)}
        onOpenExisting={(customerId) => setExistingCustomerId(customerId)}
      />
    </>
  );
}

export default BookOrderPage;
